/**
 * PATH: packages/ui/src/kanban/components/KanbanCardMobileMoreButton.tsx
 * FILE_ID: UI-KANBAN-CARD-MOBILE-MORE-001
 * PURPOSE: Phone-only "⋯" on the card footer. Opens the colour / tools rail
 *   when the host wired quickActions; otherwise falls back to the actions menu.
 * WHY:
 *   Click, long-press, and kebab must stay exclusive. The kebab used to fire
 *   the same onContextMenu as a hold, so colour chrome and the actions sheet
 *   stacked. Operator: kebab = colour; hold = actions; tap = card detail.
 * RELATED:
 *   - ./KanbanCard.tsx: footer trailingSlot on phones.
 *   - ./kanbanCardSurfaces.ts: gesture → surface contract.
 */
import { MoreHorizontal } from 'lucide-react'

import type { Card } from '../types'

export interface KanbanCardMobileMoreButtonProps {
  card: Card
  /** True when this tap should open colour / tools (not the actions menu). */
  chromeMode?: boolean
  onPress: (anchor: { x: number; y: number }) => void
}

export function KanbanCardMobileMoreButton({
  chromeMode = false,
  onPress,
}: KanbanCardMobileMoreButtonProps) {
  const label = chromeMode ? 'Card colour and tools' : 'More actions'
  return (
    <button
      type="button"
      data-card-action="more"
      aria-label={label}
      title={label}
      onClick={(e) => {
        e.stopPropagation()
        e.preventDefault()
        const r = e.currentTarget.getBoundingClientRect()
        onPress({ x: r.right, y: r.bottom })
      }}
      onMouseDown={(e) => e.stopPropagation()}
      onPointerDown={(e) => e.stopPropagation()}
      onDoubleClick={(e) => e.stopPropagation()}
      className="-my-1.5 -mr-1 inline-flex min-h-[34px] min-w-[34px] items-center justify-center rounded-lg text-neutral-500 transition-transform active:scale-90 active:bg-neutral-100 dark:text-zinc-400 dark:active:bg-zinc-800"
    >
      <MoreHorizontal className="h-[18px] w-[18px]" aria-hidden="true" />
    </button>
  )
}
